import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, UserCheck, UserPlus } from "lucide-react";
import { Button, cn } from "@wa/ui";
import type { ArDetail } from "../../../lib/ariregister";
import {
  createClient,
  createLead,
  duplicateRegCode,
  lookupByRegCode,
  type CrmClientMatch,
  type CrmLeadMatch,
} from "../../../lib/clients";
import { webHref } from "../../../lib/webUrl";

type T = (k: string, vars?: Record<string, string | number>) => string;

/** Checking -> we know -> (the lookup was refused or failed). */
type LookupState =
  | { kind: "checking" }
  | { kind: "done"; lead: CrmLeadMatch | null; client: CrmClientMatch | null }
  | { kind: "failed" };

/**
 * What this register company already is in the CRM, and what it can still
 * become.
 *
 * The register page is where a business first turns into work, so it carries
 * the two buttons that file it: as a lead, or straight as a client. Both used
 * to be offered unconditionally - the page knew nothing about the CRM - and a
 * company opened twice was filed twice, which is how two identical cards ended
 * up in the "new" column of the board with one registry code between them.
 *
 * So the page asks first (by registry code, the only field a company cannot
 * share with another) and offers the button that fits the answer: file it, or
 * open the record that already exists. The check is not the safeguard, though -
 * it is only what the operator sees. A stale screen, a second tab and a
 * double-click all still reach the API, and the create endpoints refuse a
 * duplicate registry code there with the existing id in hand; the buttons below
 * follow that id rather than reporting a failure nobody can act on.
 *
 * If the lookup itself is refused (`view:crm` is a separate right from
 * `manage:crm.clients`) the plain offer comes back, exactly as it was. That is
 * the honest fallback: this component cannot show what it may not read, and the
 * server still holds the line.
 */
export function CrmActions({
  entity,
  t,
  canView,
  canManage,
}: {
  entity: ArDetail;
  t: T;
  canView: boolean;
  canManage: boolean;
}) {
  const nav = useNavigate();
  const [state, setState] = useState<LookupState>({ kind: "checking" });
  const [busy, setBusy] = useState<"none" | "lead" | "client">("none");
  const regCode = entity.regCode;

  useEffect(() => {
    if (!canView) {
      setState({ kind: "failed" });
      return;
    }
    const ctrl = new AbortController();
    setState({ kind: "checking" });
    lookupByRegCode(regCode, ctrl.signal)
      .then((res) => setState({ kind: "done", lead: res.leads[0] ?? null, client: res.clients[0] ?? null }))
      .catch(() => {
        if (!ctrl.signal.aborted) setState({ kind: "failed" });
      });
    return () => ctrl.abort();
  }, [regCode, canView]);

  // Create a lead prefilled from the register data and open it. A registry code
  // we already hold comes back as a 409 carrying that record's id - the same
  // destination the "open" button would have used, so a race ends on the lead
  // that exists instead of on an error.
  async function addAsLead() {
    setBusy("lead");
    try {
      const lead = await createLead({
        name: entity.name,
        kind: "company",
        regCode: entity.regCode,
        vatNumber: entity.vatNumber ?? undefined,
        email: entity.email ?? undefined,
        phone: entity.phone ?? undefined,
        // Normalised on the way in: once saved this is our own record, not a
        // mirror of the register, so it should hold an address that works.
        website: webHref(entity.webUrl) ?? undefined,
        address: entity.addressFull ?? undefined,
        source: "ariregister",
      });
      nav(`/leads/${lead.id}`);
    } catch (err) {
      const dup = duplicateRegCode(err);
      if (dup?.leadId) return nav(`/leads/${dup.leadId}`);
      setBusy("none");
    }
  }

  async function addAsClient() {
    setBusy("client");
    try {
      const client = await createClient({
        name: entity.name,
        kind: "company",
        regCode: entity.regCode,
        vatNumber: entity.vatNumber ?? undefined,
        email: entity.email ?? undefined,
        phone: entity.phone ?? undefined,
        website: webHref(entity.webUrl) ?? undefined,
        addressLine: entity.addressFull ?? undefined,
      });
      nav(`/clients/${client.id}`);
    } catch (err) {
      const dup = duplicateRegCode(err);
      if (dup?.clientId) return nav(`/clients/${dup.clientId}`);
      setBusy("none");
    }
  }

  if (!canView && !canManage) return null;

  const lead = state.kind === "done" ? state.lead : null;
  const client = state.kind === "done" ? state.client : null;
  // Nothing is offered before the answer arrives: an "add" the operator can
  // press while the page is still asking is the bug this component exists for.
  const waiting = state.kind === "checking" || busy !== "none";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {lead ? (
        <Button variant="outline" size="sm" onClick={() => nav(`/leads/${lead.id}`)}>
          <UserCheck className="size-3.5 text-primary" /> {t("ariregister.detail.openLead")}
        </Button>
      ) : (
        canManage && (
          <Button variant="outline" size="sm" onClick={addAsLead} disabled={waiting}>
            <UserPlus className={cn("size-3.5", busy === "lead" && "animate-pulse")} />{" "}
            {t("ariregister.detail.addLead")}
          </Button>
        )
      )}

      {client ? (
        <Button variant="outline" size="sm" onClick={() => nav(`/clients/${client.id}`)}>
          <Briefcase className="size-3.5 text-primary" /> {t("ariregister.detail.openClient")}
        </Button>
      ) : (
        canManage && (
          <Button variant="outline" size="sm" onClick={addAsClient} disabled={waiting}>
            <Briefcase className={cn("size-3.5", busy === "client" && "animate-pulse")} />{" "}
            {t("ariregister.detail.addClient")}
          </Button>
        )
      )}
    </div>
  );
}
